import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { CheckOutlined, FileTextOutlined } from "@ant-design/icons";

import { Card } from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { CompassModal } from "../../components/common/Modal";
import { DetailText } from "../../components/visuals/DetailAtoms";
import { EscalationAvatar } from "../../components/visuals/Escalation";
import { EASE, popIn, reducedMotion } from "../../components/visuals/motion";
import {
  SIGNAL_ICON_PX,
  SignalIconSize,
  SignalTabItem,
  SignalTabs,
  avatarIcon,
} from "../../components/visuals/SignalTabs";
import {
  compassLightGreen,
  darkGreen,
  ESCALATION_COLORS,
  greyText,
  lightGreyOutline,
  lightGreys,
  lightGreyText,
  milestoneTagsColors,
  statusBadgeColors,
} from "../../styles";
import { BadgeStatus, MilestoneKey } from "../../enums/Status";
import {
  EscalationParentType,
  formatEscalationCode,
} from "../../enums/Escalations";
import { ESCALATION_LABELS } from "../../utils/escalationLabels";
import { formatDateShort } from "../../utils/formattingUtils";
import {
  MILESTONE_ICONS,
  MilestoneItem,
} from "../CompanyEngagements/shared/MilestoneCard";

const LINE_H = 20;
const DOT = 7;
const PAD_Y = 5;
const PAD_X = 8;
const NODE_Y = PAD_Y + LINE_H / 2;
const RAIL_X = PAD_X + DOT / 2 - 0.5;
const MILESTONE_KEYS: MilestoneKey[] = [
  MilestoneKey.M1,
  MilestoneKey.M2,
  MilestoneKey.M3,
  MilestoneKey.M4,
];

const fillIn = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const Strip = styled.div`
  display: flex;
  gap: 3px;
  margin-top: 12px;
`;

const Segment = styled.span<{ $tint: string; $on: boolean; $index: number }>`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: ${({ $tint }): string => $tint};
  opacity: ${({ $on }): number => ($on ? 1 : 0.35)};
  transform-origin: left center;
  animation: ${fillIn} 0.45s ${EASE} both;
  animation-delay: ${({ $index }): number => $index * 60}ms;
  ${reducedMotion}
`;

const StripLegend = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 10px;
  color: ${lightGreyText};
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const HeadMeta = styled.span`
  font-family: "Apercu";
  font-size: 12px;
  color: ${lightGreyText};
  white-space: nowrap;
`;

const List = styled.div`
  margin: 8px -${PAD_X}px 0;
`;

const Row = styled.button<{
  $index: number;
  $above: string | null;
  $below: string | null;
  $wash?: string;
}>`
  all: unset;
  position: relative;
  display: grid;
  grid-template-columns: ${DOT}px minmax(0, 1fr);
  column-gap: 14px;
  width: 100%;
  padding: ${PAD_Y}px ${PAD_X}px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${({ $wash }): string => $wash ?? "transparent"};
  cursor: pointer;
  transition:
    background 0.15s ease,
    filter 0.15s ease;
  animation: ${popIn} 0.3s ${EASE} both;
  animation-delay: ${({ $index }): number => 60 + $index * 45}ms;
  ${reducedMotion}

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: ${RAIL_X}px;
    width: 1px;
  }

  &::before {
    display: ${({ $above }): string => ($above ? "block" : "none")};
    top: 0;
    height: ${NODE_Y}px;
    background: ${({ $above }): string => $above ?? "transparent"};
  }

  &::after {
    display: ${({ $below }): string => ($below ? "block" : "none")};
    top: ${NODE_Y}px;
    bottom: 0;
    background: ${({ $below }): string => $below ?? "transparent"};
  }

  &:hover {
    background: ${({ $wash }): string => $wash ?? lightGreys.KPIBackground};
    filter: ${({ $wash }): string => ($wash ? "saturate(1.4)" : "none")};
  }

  &:focus-visible {
    outline: 2px solid ${compassLightGreen};
    outline-offset: -2px;
  }
`;

const NodeSlot = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  width: ${DOT}px;
  height: ${LINE_H}px;
`;

const Pin = styled.span<{ $fill: string }>`
  width: ${DOT}px;
  height: ${DOT}px;
  border-radius: 50%;
  background: ${({ $fill }): string => $fill};
  transition: transform 0.18s ${EASE};

  button:hover & {
    transform: scale(1.2);
  }
`;

const Notches = styled.span`
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
`;

const Notch = styled.span<{ $fill: string; $on: boolean }>`
  width: 5px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $fill, $on }): string => ($on ? $fill : lightGreyOutline)};
`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const Line = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${LINE_H}px;
  min-width: 0;
`;

const Name = styled.span<{ $idle?: boolean }>`
  font-family: ${({ $idle }): string => ($idle ? "Apercu" : "ApercuMedium")};
  font-size: 13px;
  color: ${({ $idle }): string => ($idle ? greyText : darkGreen)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Spacer = styled.span`
  flex: 1;
  min-width: 8px;
`;

const Mono = styled.span<{ $strong?: boolean }>`
  flex-shrink: 0;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${({ $strong }): string => ($strong ? darkGreen : lightGreyText)};
`;

const DescMark = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  font-size: 11px;
  color: ${lightGreyText};
`;

const Snippet = styled.span`
  font-family: "Apercu";
  font-size: 12px;
  line-height: 1.5;
  color: ${greyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Apercu";
  font-size: 12px;
  color: ${greyText};
  white-space: nowrap;
`;

const Dot = styled.span<{ $tint: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tint }): string => $tint};
`;

const Sep = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${lightGreyOutline};
`;

const ModalBody = styled.div`
  padding: 0 28px 24px;
`;

const Measure = styled.div<{ $on: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  opacity: ${({ $on }): number => ($on ? 1 : 0.55)};

  & + & {
    border-top: 1px solid ${lightGreyOutline};
  }
`;

const Check = styled.span<{ $tint: string; $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 50%;
  background: ${({ $tint, $on }): string => ($on ? $tint : "white")};
  box-shadow: inset 0 0 0 1px
    ${({ $on }): string => ($on ? "rgba(21, 50, 48, 0.08)" : lightGreyOutline)};
  font-size: 8px;
  color: ${darkGreen};
`;

const MeasureText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const MeasureName = styled.span`
  font-family: "ApercuMedium";
  font-size: 13px;
  color: ${darkGreen};
`;

const MeasureDesc = styled.span`
  font-family: "Apercu";
  font-size: 12px;
  line-height: 1.5;
  color: ${greyText};
`;

const statusOf = (milestone: MilestoneItem): BadgeStatus =>
  milestone.status || BadgeStatus.PLANNED;

const tintOf = (status: BadgeStatus): string =>
  status === BadgeStatus.PLANNED
    ? lightGreyOutline
    : statusBadgeColors[status].text;

const startLabel = (milestone: MilestoneItem): string => {
  const date = formatDateShort(milestone.startDate);
  return date ? `Started ${date}` : "Not started";
};

const activeMeasures = (parent: EscalationParentType): string[] =>
  parent.escalations
    .filter((e) => e.isActive)
    .map((e) => ESCALATION_LABELS[e.key]);

const milestoneTone = (milestoneId: number): { bg: string; text: string } =>
  milestoneTagsColors[MILESTONE_KEYS[milestoneId - 1] ?? MilestoneKey.M1];

const RailCard = ({
  title,
  meta,
  strip,
  isLoading,
  children,
}: {
  title: string;
  meta: string;
  strip: React.ReactNode;
  isLoading: boolean;
  children: React.ReactNode;
}): JSX.Element => (
  <Card isLoading={isLoading} w={100} h="auto" m="0">
    <Head>
      <CompassParagraph fontSize={16} bold>
        {title}
      </CompassParagraph>
      <HeadMeta>{meta}</HeadMeta>
    </Head>
    {strip}
    <List>{children}</List>
  </Card>
);

const MilestoneNode = ({
  status,
  milestoneId,
}: {
  status: BadgeStatus;
  milestoneId: number;
}): JSX.Element => {
  const tone = milestoneTone(milestoneId);
  return (
    <Pin $fill={status === BadgeStatus.PLANNED ? lightGreyOutline : tone.bg} />
  );
};

interface MilestoneRailProps {
  milestones: MilestoneItem[];
  isLoading: boolean;
}

export const MilestoneRail = ({
  milestones,
  isLoading,
}: MilestoneRailProps): JSX.Element => {
  const [focus, setFocus] = useState<number | null>(null);
  const current = milestones.reduce(
    (best, m) =>
      statusOf(m) === BadgeStatus.PLANNED
        ? best
        : Math.max(best, m.milestoneId),
    1,
  );
  const segment = (i: number): string =>
    statusOf(milestones[i]) === BadgeStatus.COMPLETE
      ? milestoneTone(milestones[i].milestoneId).bg
      : lightGreyOutline;

  const items: SignalTabItem[] = milestones.map((m) => {
    const status = statusOf(m);
    return {
      key: `m${m.milestoneId}`,
      label: m.milestone,
      kind: `Milestone ${m.milestoneId} · ${status}`,
      renderIcon: avatarIcon(m.milestone, MILESTONE_ICONS[m.milestoneId - 1]),
      detail: (
        <>
          <Meta>
            <Dot $tint={tintOf(status)} />
            {status}
            <Sep />
            {startLabel(m)}
          </Meta>
          <DetailText>{m.description || "—"}</DetailText>
        </>
      ),
    };
  });

  return (
    <RailCard
      title="Milestones"
      meta={`${current} of ${milestones.length}`}
      strip={
        <Strip>
          {milestones.map((m, i) => (
            <Segment
              key={m.milestoneId}
              $index={i}
              $tint={milestoneTone(m.milestoneId).bg}
              $on={m.milestoneId <= current}
            />
          ))}
        </Strip>
      }
      isLoading={isLoading}
    >
      {milestones.map((m, i) => {
        const status = statusOf(m);
        const idle = status === BadgeStatus.PLANNED;
        const description = m.description?.trim() ?? "";
        return (
          <Row
            key={m.milestoneId}
            type="button"
            $index={i}
            $above={i > 0 ? segment(i - 1) : null}
            $below={i < milestones.length - 1 ? segment(i) : null}
            onClick={(): void => setFocus(i)}
          >
            <NodeSlot>
              <MilestoneNode status={status} milestoneId={m.milestoneId} />
            </NodeSlot>
            <Body>
              <Line>
                <Name $idle={idle}>{m.milestone}</Name>
                <Spacer />
                {description && (
                  <DescMark aria-label="Has description">
                    <FileTextOutlined />
                  </DescMark>
                )}
                <Mono>M{m.milestoneId}</Mono>
              </Line>
              {description && <Snippet>{description}</Snippet>}
              {!idle && (
                <Meta>
                  {status}
                  <Sep />
                  {startLabel(m)}
                </Meta>
              )}
            </Body>
          </Row>
        );
      })}
      <CompassModal
        show={focus !== null}
        handleClose={(): void => setFocus(null)}
        size="lg"
        title="Milestones"
        maxHeight={720}
      >
        {focus !== null && (
          <ModalBody>
            <SignalTabs
              key={focus}
              items={items}
              initialIndex={focus}
              ariaLabel="Milestones"
            />
          </ModalBody>
        )}
      </CompassModal>
    </RailCard>
  );
};

interface EscalationRailProps {
  escalations: EscalationParentType[];
  isLoading: boolean;
}

export const EscalationRail = ({
  escalations,
  isLoading,
}: EscalationRailProps): JSX.Element => {
  const [focus, setFocus] = useState<number | null>(null);
  const total = escalations.reduce(
    (sum, p) => sum + activeMeasures(p).length,
    0,
  );
  const peak = escalations.reduce(
    (best, p) => (activeMeasures(p).length > 0 ? Math.max(best, p.key) : best),
    0,
  );
  const segment = (level: number): string =>
    ESCALATION_COLORS[level + 1] ?? lightGreyOutline;

  const items: SignalTabItem[] = escalations.map((parent) => ({
    key: `e${parent.key}`,
    label: parent.title,
    kind: `Escalation ${parent.key} · ${activeMeasures(parent).length} of ${
      parent.escalations.length
    } active`,
    renderIcon: (size: SignalIconSize): JSX.Element => (
      <EscalationAvatar code={`E${parent.key}`} size={SIGNAL_ICON_PX[size]} />
    ),
    detail: (
      <div>
        {parent.escalations.map((e) => (
          <Measure key={e.key} $on={e.isActive}>
            <Check $tint={ESCALATION_COLORS[parent.key]} $on={e.isActive}>
              {e.isActive && <CheckOutlined />}
            </Check>
            <MeasureText>
              <MeasureName>{ESCALATION_LABELS[e.key]}</MeasureName>
              <MeasureDesc>{e.description}</MeasureDesc>
            </MeasureText>
            <Mono>{formatEscalationCode(e.key)}</Mono>
          </Measure>
        ))}
      </div>
    ),
  }));

  return (
    <RailCard
      title="Escalations"
      meta={total > 0 ? `${total} active · peak E${peak}` : "None active"}
      strip={
        <>
          <Strip>
            {escalations.map((parent, i) => (
              <Segment
                key={parent.key}
                $index={i}
                $tint={ESCALATION_COLORS[parent.key]}
                $on={parent.key <= peak}
              />
            ))}
          </Strip>
          <StripLegend>
            <span>Dialogue</span>
            <span>Divest</span>
          </StripLegend>
        </>
      }
      isLoading={isLoading}
    >
      {escalations.map((parent, i) => {
        const active = activeMeasures(parent);
        const idle = active.length === 0;
        return (
          <Row
            key={parent.key}
            type="button"
            $index={i}
            $above={i > 0 ? segment(parent.key - 1) : null}
            $below={i < escalations.length - 1 ? segment(parent.key) : null}
            $wash={idle ? undefined : `${ESCALATION_COLORS[parent.key]}59`}
            onClick={(): void => setFocus(i)}
          >
            <NodeSlot>
              <Pin $fill={ESCALATION_COLORS[parent.key]} />
            </NodeSlot>
            <Body>
              <Line>
                <Name $idle={idle}>{parent.title}</Name>
                <Spacer />
                <Notches aria-hidden="true">
                  {parent.escalations.map((e) => (
                    <Notch
                      key={e.key}
                      $fill={ESCALATION_COLORS[parent.key]}
                      $on={e.isActive}
                    />
                  ))}
                </Notches>
                <Mono $strong={!idle}>E{parent.key}</Mono>
              </Line>
              {!idle && <Snippet>{active.join(", ")}</Snippet>}
            </Body>
          </Row>
        );
      })}
      <CompassModal
        show={focus !== null}
        handleClose={(): void => setFocus(null)}
        size="lg"
        title="Escalation measures"
        maxHeight={720}
      >
        {focus !== null && (
          <ModalBody>
            <SignalTabs
              key={focus}
              items={items}
              initialIndex={focus}
              ariaLabel="Escalation levels"
            />
          </ModalBody>
        )}
      </CompassModal>
    </RailCard>
  );
};
